import { Image } from 'expo-image';
import { useEffect } from 'react';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';

import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import { GiphyPickerModal } from '@/src/features/giphy/GiphyPickerModal';
import {
  isNetworkAvailable,
  isRecoverableNetworkError,
  queueCreateComment,
} from '@/src/features/offline/outbox-service';
import {
  MAX_COMMENT_BODY_LENGTH,
  MAX_VIDEO_DURATION_MILLIS,
  MAX_VIDEO_DURATION_SECONDS,
  type PublicPost,
} from '@/src/features/posts/post-service';
import {
  useCreatePostComment,
  useDeletePostComment,
  usePostComments,
} from '@/src/features/posts/use-post-comments';
import { getProfileAvatarURL } from '@/src/features/profile/profile-service';
import {
  useProfile,
  usePublicProfile,
} from '@/src/features/profile/use-profile';
import { maybeCorrectWriting } from '@/src/features/writing-assist/writing-assist-service';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import { CommentComposer, type CommentComposerProps } from './CommentComposer';
import { COMMENT_MODAL_COMPACT_BREAKPOINT } from './CommentModalShell';
import { type CommentReplyTarget } from './comment-threading';
import { PostCommentThreadList } from './PostCommentThreadList';
import { ReplyCommentComposer } from './ReplyCommentComposer';
import { CommentThreadModal } from './CommentThreadModal';
import { useCommentComposerState } from './use-comment-composer-state';
import { useCommentSortPreference } from './use-comment-sort-preference';

function PostPreview({ post }: { post: PublicPost }) {
  const authorProfileQuery = usePublicProfile(post.authorUid);
  const authorProfile = authorProfileQuery.data;
  const title =
    authorProfile?.fullName || post.authorFullName || 'Frienzzi user';
  const authorUsername = authorProfile?.username ?? post.authorUsername;
  const authorPhotoURL =
    getProfileAvatarURL(authorProfile, 'thumb') ?? post.authorPhotoURL;
  const body = post.body?.trim();

  return (
    <View style={styles.preview}>
      <View style={styles.previewAvatar}>
        {authorPhotoURL ? (
          <Image
            accessibilityLabel=""
            cachePolicy="memory-disk"
            contentFit="cover"
            source={{ uri: authorPhotoURL }}
            style={styles.previewAvatarImage}
          />
        ) : (
          <Text style={styles.previewAvatarText}>
            {title.slice(0, 1).toUpperCase()}
          </Text>
        )}
      </View>
      <View style={styles.previewText}>
        <View style={styles.previewAuthorRow}>
          <Text numberOfLines={1} style={styles.previewAuthor}>
            {title}
          </Text>
          {authorUsername ? (
            <Text numberOfLines={1} style={styles.previewUsername}>
              @{authorUsername}
            </Text>
          ) : null}
        </View>
        {body ? (
          <Text numberOfLines={3} style={styles.previewBody}>
            {body}
          </Text>
        ) : (
          <Text style={styles.previewBody} numberOfLines={2}>
            Reply to this post on Frienzzi.
          </Text>
        )}
      </View>
    </View>
  );
}

export function CommentComposeModal({
  initialReplyTarget = null,
  onClose,
  post,
  visible,
}: {
  initialReplyTarget?: CommentReplyTarget | null;
  onClose: () => void;
  post: PublicPost | null;
  visible: boolean;
}) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const { width: viewportWidth } = useWindowDimensions();
  const profileQuery = useProfile(user?.uid);
  const createComment = useCreatePostComment();
  const deleteComment = useDeletePostComment();
  const { setSortMode, sortMode } = useCommentSortPreference();
  const commentsState = usePostComments(post?.id, user?.uid, 50, {
    enabled: visible && Boolean(post?.id && user?.uid),
  });
  const commentComposerState = useCommentComposerState({
    giphySelectEventName: 'giphy_select',
    maxVideoDurationMillis: MAX_VIDEO_DURATION_MILLIS,
    maxVideoDurationSeconds: MAX_VIDEO_DURATION_SECONDS,
    mediaPasteEventName: 'comment_media_paste',
    mediaSelectEventName: 'comment_media_select',
    showError,
  });
  const {
    attachMedia,
    attachPastedImage,
    beginFreshReply,
    beginReply,
    body,
    canSubmit,
    clearMedia,
    closeGiphy,
    giphyGif,
    giphyVisible,
    handleComposerFocusChange,
    inputRef,
    openGiphy,
    profileMentions,
    replyTarget,
    resetComposer,
    selectGiphy,
    setBody,
    setGiphyVisible,
    setProfileMentions,
    setReplyTarget,
    setUploadStatus,
    uploadMedia,
    uploadStatus,
  } = commentComposerState;
  const fullName =
    profileQuery.data?.fullName || user?.email || 'Frienzzi user';
  const avatarInitial = fullName.slice(0, 1).toUpperCase();
  const avatarURL = getProfileAvatarURL(profileQuery.data, 'thumb');
  const compactModal = viewportWidth < COMMENT_MODAL_COMPACT_BREAKPOINT;
  const commentOrderSessionKey = `${post?.id ?? 'no-post'}:${
    visible ? 'open' : 'closed'
  }`;

  useEffect(() => {
    if (!visible) {
      resetComposer();
      setGiphyVisible(false);
    }
  }, [resetComposer, setGiphyVisible, visible]);

  useEffect(() => {
    resetComposer();
  }, [post?.id, resetComposer]);

  useEffect(() => {
    if (!visible || !initialReplyTarget) {
      return;
    }

    beginFreshReply(initialReplyTarget, 120);
  }, [beginFreshReply, initialReplyTarget, visible]);

  function handleClose() {
    onClose();
  }

  async function submitComment() {
    const trimmedBody = body.trim();

    if (!user?.uid) {
      showError(new Error('You must be signed in to comment.'));
      return;
    }

    if (!post?.id || uploadStatus || createComment.isPending) {
      return;
    }

    if (!trimmedBody && !uploadMedia && !giphyGif) {
      showError(
        new Error('Add a comment, photo, video, or GIF before posting.'),
      );
      return;
    }

    if (trimmedBody.length > MAX_COMMENT_BODY_LENGTH) {
      showError(new Error('Comments must be 1,000 characters or fewer.'));
      return;
    }

    const uploadLabel =
      uploadMedia?.kind === 'image'
        ? 'photo'
        : uploadMedia?.kind === 'video'
          ? 'video'
          : null;
    let finalBody = trimmedBody;
    const commentInput = {
      authorUid: user.uid,
      authorFounderBadge: profileQuery.data?.founderBadge === true,
      authorFullName: fullName,
      authorPhotoURL: avatarURL,
      authorUsername: profileQuery.data?.username ?? null,
      authorVerifiedBadge:
        profileQuery.data?.verification?.badge?.verified === true,
      body: finalBody,
      giphyGif,
      mediaAsset: uploadMedia?.asset ?? null,
      mediaKind: uploadMedia?.kind ?? null,
      parentCommentId: replyTarget?.parentCommentId ?? null,
      postId: post.id,
      profileMentions,
      replyToCommentId: replyTarget?.replyToCommentId ?? null,
    };

    try {
      setUploadStatus(uploadLabel ? `Preparing ${uploadLabel}...` : null);

      if (!(await isNetworkAvailable())) {
        await queueCreateComment(commentInput);
        trackEvent('comment_queue', {
          has_media: Boolean(uploadMedia || giphyGif),
          has_text: Boolean(trimmedBody),
          media_kind: uploadMedia?.kind ?? (giphyGif ? 'gif' : 'none'),
        });
        resetComposer();
        showSuccess(
          'Your comment will upload when your connection is back.',
          'Comment queued',
        );
        return;
      }

      if (finalBody) {
        setUploadStatus('Checking spelling...');

        try {
          const correction = await maybeCorrectWriting({
            profile: profileQuery.data,
            surface: 'comment',
            text: finalBody,
          });

          finalBody = correction.text;
          commentInput.body = finalBody;

          if (correction.changed) {
            trackEvent('writing_assist_correction_apply', {
              surface: 'comment',
            });
          }
        } catch {
          trackEvent('writing_assist_correction_skip', {
            reason: 'error',
            surface: 'comment',
          });
        }

        setUploadStatus(uploadLabel ? `Preparing ${uploadLabel}...` : null);
      }

      createComment.mutate(
        {
          ...commentInput,
        },
        {
          onError: (error) => {
            if (isRecoverableNetworkError(error)) {
              void queueCreateComment(commentInput);
              showSuccess(
                'Your comment will upload when your connection is back.',
                'Comment queued',
              );
              return;
            }

            showError(error, 'Unable to post your comment. Please try again.');
          },
          onSuccess: () => {
            trackEvent('comment_create', {
              has_media: Boolean(uploadMedia || giphyGif),
              has_text: Boolean(trimmedBody),
              media_kind: uploadMedia?.kind ?? (giphyGif ? 'gif' : 'none'),
            });
            resetComposer();
            showSuccess('Your comment is posting.', 'Comment posted');
          },
        },
      );
    } catch (error) {
      if (isRecoverableNetworkError(error)) {
        await queueCreateComment(commentInput);
        resetComposer();
        showSuccess(
          'Your comment will upload when your connection is back.',
          'Comment queued',
        );
        return;
      }

      showError(error, 'Unable to post your comment. Please try again.');
    } finally {
      setUploadStatus(null);
    }
  }

  async function deleteThreadComment(commentId: string) {
    if (!post?.id) {
      return;
    }

    try {
      await deleteComment.mutateAsync({ commentId, postId: post.id });
    } catch (error) {
      showError(error, 'Unable to delete that comment.');
      return;
    }

    showSuccess('Comment deleted.', 'Post updated');
  }

  if (!post || !user?.uid) {
    return null;
  }

  const composerProps: Omit<CommentComposerProps, 'compact'> = {
    avatarInitial,
    avatarURL,
    body,
    canSubmit,
    currentUid: user.uid,
    giphyGif,
    inputRef,
    loading: Boolean(uploadStatus || createComment.isPending),
    maxLength: MAX_COMMENT_BODY_LENGTH,
    mentionScope: { kind: 'comment', postId: post.id },
    profileMentions,
    uploadMedia,
    uploadStatus,
    onAttachPhoto: () => {
      void attachMedia('image');
    },
    onAttachVideo: () => {
      void attachMedia('video');
    },
    onChangeBody: setBody,
    onChangeFocus: handleComposerFocusChange,
    onChangeMentions: setProfileMentions,
    onClearMedia: clearMedia,
    onOpenGiphy: openGiphy,
    onPasteImageAsset: attachPastedImage,
    onSubmit: () => {
      void submitComment();
    },
  };
  const composer = <CommentComposer {...composerProps} compact />;

  const replyComposer = replyTarget ? (
    <ReplyCommentComposer
      {...composerProps}
      compact={compactModal}
      replyTarget={replyTarget}
      onCancelReply={() => setReplyTarget(null)}
    />
  ) : null;

  return (
    <>
      <CommentThreadModal
        closeDisabled={false}
        commentCount={commentsState.comments.length}
        compact={compactModal}
        composer={replyTarget ? null : composer}
        emptyText="Be the first to comment on this post."
        error={commentsState.error}
        loading={commentsState.loading}
        preview={<PostPreview post={post} />}
        sortMode={sortMode}
        visible={visible}
        onChangeSortMode={setSortMode}
        onClose={handleClose}
      >
        <PostCommentThreadList
          comments={commentsState.comments}
          currentUid={user.uid}
          deleteDisabled={deleteComment.isPending}
          linkPreviewSource="comment_modal_link_preview"
          mediaLayout="aspect"
          mentionSource="comment_modal_mention"
          onDelete={(commentId) => {
            void deleteThreadComment(commentId);
          }}
          onReply={beginReply}
          orderSessionKey={commentOrderSessionKey}
          postAuthorUid={post.authorUid}
          postId={post.id}
          replyComposer={replyComposer}
          replyTarget={replyTarget}
          showSyncStatus
          sortMode={sortMode}
        />
      </CommentThreadModal>
      <GiphyPickerModal
        visible={giphyVisible}
        onClose={closeGiphy}
        onSelect={selectGiphy}
      />
    </>
  );
}

const styles = StyleSheet.create({
  preview: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
  },
  previewAuthor: {
    color: colors.text,
    flexShrink: 1,
    fontSize: 15,
    fontWeight: '900',
    minWidth: 0,
  },
  previewAuthorRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
    minWidth: 0,
  },
  previewAvatar: {
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: 20,
    height: 40,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 40,
  },
  previewAvatarImage: {
    height: '100%',
    width: '100%',
  },
  previewAvatarText: {
    color: colors.secondary,
    fontSize: 16,
    fontWeight: '900',
  },
  previewBody: {
    color: colors.secondary,
    fontSize: 15,
    lineHeight: 21,
  },
  previewText: {
    flex: 1,
    gap: spacing.xs,
    minWidth: 0,
  },
  previewUsername: {
    color: colors.mutedText,
    flexShrink: 1,
    fontSize: 15,
  },
});
